import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root',
})
export class BdsDxOdataParser {
    createBinaryOperationFormatter = (op: any) => (prop: any, val: any) => `${prop} ${op} ${val}`;
    serializePropName = (propName: any) => propName.replace(/\./g, '/');
    createStringFuncFormatter = (op: any) => (prop: string | string[], val: string) => {
        const bag = [op, '('];
        prop = -1 === prop.indexOf('tolower(') ? `tolower(${prop})` : prop;
        val = val.toLowerCase();
        bag.push(prop, ',', val);
        bag.push(')');
        return bag.join('');
    };

    STRING_FUNCTIONS = ['contains', 'notcontains', 'startswith', 'endswith'];
    formatters: Map<string, (prop: any, val: any) => string> = new Map([
        ['=', this.createBinaryOperationFormatter('eq')],
        ['<>', this.createBinaryOperationFormatter('ne')],
        ['>', this.createBinaryOperationFormatter('gt')],
        ['>=', this.createBinaryOperationFormatter('ge')],
        ['<', this.createBinaryOperationFormatter('lt')],
        ['<=', this.createBinaryOperationFormatter('le')],
        ['startswith', this.createStringFuncFormatter('startswith')],
        ['endswith', this.createStringFuncFormatter('endswith')],
        ['contains', this.createStringFuncFormatter('contains')],
        ['notcontains', this.createStringFuncFormatter('not contains')],
    ]);

    constructor() {}

    buildODataQueryString(params: any): string {
        // Convert params to OData query string
        let queryString = '';
        if (params.filter) {
            queryString += `$filter=${this.compileFilter(params.filter)}&`;
        }
        if (params.sort) {
            queryString += `$orderby=${params.sort
                .map((s) => `${s.selector} ${s.desc ? 'desc' : 'asc'}`)
                .join(',')}&`;
        }
        if (params.skip) {
            queryString += `$skip=${params.skip}&`;
        }
        if (params.take) {
            queryString += `$top=${params.take}&`;
        }
        if (params.requireTotalCount) {
            queryString += `$count=true&`;
        }

        // Add other OData query options as needed
        return queryString.slice(0, -1); // Remove trailing '&'
    }

    compileFilter(filter: any[]) {
        if (Array.isArray(filter[0])) {
            return this.compileGroup(filter);
        }
        if (this.isUnaryOperation(filter)) {
            const op = filter[0];
            const crit: string = this.compileFilter(filter[1]);
            if ('!' === op) {
                return `not (${crit})`;
            }
        }
        return this.compileBinary(filter);
    }

    isUnaryOperation(filter: any[]) {
        return '!' === filter[0] && Array.isArray(filter[1]);
    }

    isConjunctiveOperator(condition: string) {
        return /^(and|&&|&)$/i.test(condition);
    }

    normalizeBinaryCriterion(crit: any[]) {
        return [
            crit[0],
            crit.length < 3 ? '=' : String(crit[1]).toLowerCase(),
            crit.length < 2 ? true : crit[crit.length - 1],
        ];
    }

    compileGroup(filter: any[]) {
        const bag: any[] = [];
        let groupOperator: any;
        let nextGroupOperator: string;
        filter.forEach((criterion, index) => {
            if (Array.isArray(criterion)) {
                if (bag.length > 1 && groupOperator !== nextGroupOperator) {
                    throw new Error('Dev Extreme : E4019');
                }
                bag.push(`(${this.compileFilter(criterion)})`);
                groupOperator = nextGroupOperator;
                nextGroupOperator = 'and';
            } else {
                nextGroupOperator = this.isConjunctiveOperator(criterion) ? 'and' : 'or';
            }
        });
        return bag.join(` ${groupOperator} `);
    }

    compileBinary(criteria: any[]) {
        criteria = this.normalizeBinaryCriterion(criteria);
        const op: string = criteria[1];
        const fieldName = criteria[0];
        const formatter = this.formatters.get(op);

        let value = criteria[2];

        if (formatter === undefined) {
            throw new Error(`Unknown filter operation: ${op}`);
        }

        return formatter(this.serializePropName(fieldName), this.serializeValue(value));
    }

    serializeValue(value: any): string {
        if (value instanceof Date) {
            return value.toISOString();
        }
        if (Array.isArray(value)) {
            return `[${value.map((item: any) => this.serializeValue(item)).join(',')}]`;
        }
        if ('string' === typeof value) {
            return `'${value.replace(/'/g, "''")}'`;
        }
        return String(value);
    }
}
